import _, { uniqueId } from 'lodash'; // assuming you have lodash installed
import React, { useMemo } from 'react';
import { Badge, Button, Card, Col, FormControl, Row } from 'react-bootstrap';
import { Clipboard, PlusCircle, Trash, CheckCircleFill, Circle } from 'react-bootstrap-icons';
import {
  appChoicesKeys,
  additionalDocHubfieldTypes,
  additionalDocHubfieldTypesWithDropdowns
} from '../../../helpers/constants';
import TextButton from '../../TextButton';
import { copyToClipboard } from '../../../helpers/global';
import { toast } from 'react-toastify';

const FORM_FIELD_MODELS = ['Contract', 'Contact', 'Inventory'];

const Field = ({ field, onChange, onDelete, highlighted = false }) => {
  const selectedModels = Array.isArray(field.models) ? field.models : field.model ? [field.model] : [];

  const toggleModel = modelName => {
    const next = selectedModels.includes(modelName)
      ? selectedModels.filter(m => m !== modelName)
      : [...selectedModels, modelName];
    onChange({ ...field, models: next });
  };

  const dropdownOptions = Array.isArray(field.dropdownOptions) ? field.dropdownOptions : [];

  const addOption = () => {
    onChange({
      ...field,
      dropdownOptions: [...dropdownOptions, { value: '', uuid: uniqueId() }]
    });
  };

  const changeOption = (uuid, newOption) => {
    onChange({
      ...field,
      dropdownOptions: dropdownOptions.map(option => (option.uuid === uuid ? newOption : option))
    });
  };

  const deleteOption = uuid => {
    onChange({
      ...field,
      dropdownOptions: dropdownOptions.filter(option => option.uuid !== uuid)
    });
  };

  const changeOptionType = newType => {
    onChange({
      ...field,
      takeOptionsFrom: newType,
      dropdownOptions: [{ value: '', uuid: uniqueId() }],
      optionAppChoiceName: appChoicesKeys[0]
    });
  };

  const docsRefrence = useMemo(() => `{{ additionalFields | ${field.key || '-'} }}`, [field]);

  return (
    <Card
      className={`h-100${highlighted ? ' border-success' : ''}`}
      style={
        highlighted
          ? {
            boxShadow: '0 0 0 2px rgba(40, 167, 69, 0.35), 0 0 18px rgba(40, 167, 69, 0.55)',
            transition: 'box-shadow 0.2s ease'
          }
          : { transition: 'box-shadow 0.2s ease' }
      }
    >
      <Card.Header className="py-2 px-3">
        <div className="d-flex align-items-center">
          <div className="d-flex flex-wrap align-items-center flex-shrink-0 mr-2">
            <h6 className="largeFont mb-0 mr-2">Applies To:</h6>
            {FORM_FIELD_MODELS.map(modelName => {
              const active = selectedModels.includes(modelName);
              return (
                <Badge
                  key={modelName}
                  pill
                  className={`px-2 py-1 mr-1 pointer ${active ? '' : 'border'} d-flex align-items-center`}
                  variant={active ? 'primary' : 'light'}
                  style={{ cursor: 'pointer', fontWeight: 500 }}
                  onClick={() => toggleModel(modelName)}
                >
                  {active ? <CheckCircleFill className="mr-1" /> : <Circle className="mr-1" />}
                  {modelName}
                </Badge>
              );
            })}
          </div>
          <div
            className="d-flex flex-wrap justify-content-between align-items-center bg-primary-light rounded px-2 py-1 pointer mx-2 ml-auto"
            style={{ minWidth: 180 }}
            onClick={() => {
              copyToClipboard(docsRefrence);
              toast.success('Copied to clipboard');
            }}
          >
            <h6 className="midFont mb-0 text-break">{docsRefrence}</h6>
            <Clipboard className="pointer ml-1 flex-shrink-0" />
          </div>
          <Button size="sm" variant="danger" className="flex-shrink-0" onClick={onDelete}>
            <Trash />
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        <Row className="mx-0">
          <Col xs={12} sm={6} className="mb-2 pl-0 pr-sm-2">
            <h6 className="midFont">Label:</h6>
            <FormControl
              size="sm"
              type="text"
              placeholder="Label"
              value={field.label}
              onChange={e => onChange({ ...field, label: e.target.value, key: _.camelCase(e.target.value) })}
            />
          </Col>
          <Col xs={12} sm={6} className="mb-2 pr-0 pl-sm-2">
            <h6 className="midFont">Field Type:</h6>
            <FormControl
              size="sm"
              as={'select'}
              value={field.type}
              onChange={e => onChange({ ...field, type: e.target.value })}
            >
              {additionalDocHubfieldTypes.map(type => (
                <option key={type} value={type}>
                  {_.startCase(type)}
                </option>
              ))}
            </FormControl>
          </Col>

          {additionalDocHubfieldTypesWithDropdowns.includes(field.type) && (
            <Col xs={12} sm={6} className="mb-2 pl-0 pr-sm-2">
              <h6 className="midFont">Take Options From:</h6>
              <FormControl
                size="sm"
                as={'select'}
                value={field.takeOptionsFrom}
                onChange={e => {
                  changeOptionType(e.target.value);
                }}
              >
                <option value="appChoice">AppChoice</option>
                <option value="manual">Manual</option>
              </FormControl>
            </Col>
          )}

          {additionalDocHubfieldTypesWithDropdowns.includes(field.type) && (
            <Col xs={12} className="mb-1 px-0">
              {field.takeOptionsFrom === 'manual' ? (
                <>
                  <h6 className="midFont">Provide Options:</h6>
                  <div className="border rounded p-2 bg-light">
                    {dropdownOptions.length > 0 ? (
                      <Row className="mx-0">
                        {dropdownOptions.map(option => (
                          <Col xs={12} sm={6} key={option.uuid} className="px-1 mb-1">
                            <div className="d-flex align-items-center">
                              <div className="flex-grow-1">
                                <FormControl
                                  size="sm"
                                  type="text"
                                  placeholder="Option"
                                  value={option.value}
                                  onChange={e => changeOption(option.uuid, { ...option, value: e.target.value })}
                                />
                              </div>
                              <div className="pl-2">
                                <Trash
                                  size={14}
                                  className="hover-light text-danger"
                                  onClick={() => deleteOption(option.uuid)}
                                />
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    ) : (
                      <h6 className="midFont mb-0 text-center text-muted">No Options Provided!</h6>
                    )}
                  </div>
                  <div className="text-right">
                    <TextButton Icon={PlusCircle} text="Add Option" variant="success" onClick={addOption} />
                  </div>
                </>
              ) : (
                <>
                  <h6 className="midFont">App Choice Name:</h6>
                  <FormControl
                    as={'select'}
                    size="sm"
                    type="text"
                    placeholder="AppChoice"
                    value={field.optionAppChoiceName}
                    onChange={e => onChange({ ...field, optionAppChoiceName: e.target.value })}
                  >
                    {appChoicesKeys.map(a => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </FormControl>
                </>
              )}
            </Col>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
};

export default Field;
